/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import MedfusionUI from '@backpackhealth/medfusion-ui/dist/index';

import ProfileService from '../../../../apiServices/ProfileService';

import Card, { MiddleGrayCard, CardSection } from '../../../components/card/Card';
import Spacer from '../../../components/Spacer';
import IconDataConnections from '../../../components/icons/IconDataConnections';
import FlexRow from '../../../components/FlexRow';
import { TrackEvent } from '../../../../utilities/UserMetrics';
import CollapsibleCard from '../../../components/card/CollapsibleCard';
import { BackpackThemeMarkdown } from '../../../components/Markdown';
import { navigate } from '../../redux/NavigationActions';
import RouteNames from '../../RouteNames';

const sourceAndroid = IS_ANDROID_BUILD ? 'assets/medfusion-ui/medfusionui.html' : null;

interface ExistingDataConnectionState {
  providerEventTriggered: boolean;
}
interface ExistingDataConnectionProps {
  profileId: number;
  config: {};
  onLoaded(): void;
}
class ExistingDataConnection extends React.Component<ExistingDataConnectionProps, ExistingDataConnectionState> {

  private clearProviderEvent: any;

  state: ExistingDataConnectionState = {
    providerEventTriggered: false,
  };

  componentWillUnmount() {
    if (this.clearProviderEvent) {
      window.clearInterval(this.clearProviderEvent);
      this.clearProviderEvent = null;
    }
  }

  onMedfusionEvent = (eventName, e) => {
    TrackEvent.integrationEvent.medfusionEvent(eventName);

    switch (eventName) {
      case 'onOpenDialog':
        this.props.onLoaded();
        break;
      case 'onRefreshConnection':
      case 'onUpdateConnection':
        ProfileService.refreshMedfusionIntegration(this.props.profileId);
        this.setState({ providerEventTriggered: true });

        this.clearProviderEvent = window.setTimeout(() => {
          this.setState({ providerEventTriggered: false });
        }, 5000);

        break;
      case 'onCreateConnection':
        ProfileService.refreshMedfusionIntegration(this.props.profileId);
        break;
      case 'onError':
      default:
        break;
    }
  }

  render() {
    const { providerEventTriggered } = this.state;
    const config = Object.assign({}, this.props.config, {
      onEvent: this.onMedfusionEvent,
    });

    return (
      <div className="cmp-existingdataconnection">
        <Card
          className="bkgd-color-gsLight"
          hasMargins={false}
          hasDropshadow={false}
        >

          <CollapsibleCard
            title={(
              <div className="font-subhead font-color-profile">
                <div>
                  <FormattedMessage id="dataConnections.connectedHelpBlock.heading" />
                </div>
                <div className="font-footnote font-color-gsMedDark">
                  <FormattedMessage id="dataConnections.connectedHelpBlock.subheading" />
                </div>
              </div>
            )}
            isCollapsible={true}
            defaultCollapsed={true}
          >
            <CardSection hasBorder={false}>
              <BackpackThemeMarkdown phraseId="dataConnections.connectedHelpBlock.message.md" />
            </CardSection>
          </CollapsibleCard>
        </Card>

        {
          providerEventTriggered ? (
            <>
              <Spacer size="4" />
              <MiddleGrayCard hasMargins={false}>
                <CardSection hasBorder={false}>
                  <FlexRow>
                    <div className="font-headline font-color-gsLightest">
                      <FormattedMessage id="dataConnections.event.retreivingData.heading" />
                    </div>
                    <IconDataConnections color="gsLightest" />
                  </FlexRow>

                  <Spacer size="8" />

                  <div className="font-footnote font-color-gsLightest">
                    <FormattedMessage id="dataConnections.event.retreivingData.message" />
                  </div>
                </CardSection>
              </MiddleGrayCard>
            </>
          ) : null
        }

        <Spacer size="4" />

        <FormattedMessage id="dataConnections.settings.button.label">
          {
            (msg) => (
              <MedfusionUI src={sourceAndroid} config={config} settingsLabel={msg} onGoToSettings={() => { navigate(RouteNames.dataConnectionsSettings()); }} />
            )
          }
        </FormattedMessage>
      </div>
    );
  }
}

export default ExistingDataConnection;
